// ---------------------------------------------------------------------------
// Validation verdict store — ONE canonical answer to "is my app OK?".
//
// Studio runs several validation engines with independent triggers (offline
// SHACL, sync studio conformance, whole-app KG SHACL, preview runtime
// conformance). Each engine keeps its own trigger and PUBLISHES its latest
// result here; surfaces READ the derived app-health verdict instead of
// re-rendering their own dialect. Every published verdict carries freshness
// (startedAt/finishedAt/staleSince), so disagreeing engines are visibly
// stale or running rather than silently contradictory.
//
// Canonical vocabulary is the ConformanceSummaryStrip scale: ok / review /
// fix (+ idle when nothing has run).
// ---------------------------------------------------------------------------

import { useMemo } from 'react'
import { create } from 'zustand'
import type { ValidationStatus } from './types'

export type CanonicalVerdict = 'ok' | 'review' | 'fix' | 'idle'

export type VerdictSourceId =
  | 'offline-shacl'
  | 'studio-conformance'
  | 'kg-shacl'
  | 'preview-conformance'
  // Hand-edited flow saves (auto-save-flow-validation.ts) — publishes a
  // flow-scoped verdict after every persisted step edit (F6).
  | 'actionflow-edit'
  // Publish-readiness pre-flight (publish-readiness-verdict.ts) — published
  // UNSCOPED from the Publish/Deploy rails as a whole-app pre-flight ESTIMATE,
  // and REFLUXED when the server gate rejects a publish (F25). The pre-flight is
  // an estimate (it omits the server quality gate), so it is NOT symmetrical
  // with the authoritative server answer:
  //   - not-ready surface estimate → 'review' (amber, "not ship-ready yet"). A
  //     still-building draft is not broken, so it must not pin the pill red (R1).
  //   - ready surface estimate     → 'ok' (a real client evaluation contributes
  //     to the green vote; the copy frames it as a pre-flight estimate).
  //   - server reflux              → 'fix' (red, authoritative "Publishing
  //     blocked"). The ONLY producer that escalates the pill to red — and it
  //     STICKS over a passive surface re-publish until the gate inputs genuinely
  //     change, so the pill can never read greener than the server ship gate.
  | 'publish-readiness'

export interface VerdictCounts {
  pass: number
  warn: number
  fail: number
}

export interface VerdictEntry {
  /** A VerdictSourceId for engine-published entries; display layers may
   *  synthesize section-scoped entries with a `section:` prefix. */
  source: string
  label: string
  verdict: CanonicalVerdict
  score: number | null
  counts: VerdictCounts
  /** Headline of the worst failing/warning check, when the engine knows it. */
  worstCheck: string | null
  startedAt: number
  /** null while the engine is still running. */
  finishedAt: number | null
  /** Set when the engine's input changed after this result was computed. */
  staleSince: number | null
  /**
   * Display label when the result covers only part of the app (e.g.
   * 'Block: hero'). null/undefined = whole-app run. A scoped 'ok' is NOT an
   * app-level green vote — deriveAppHealth excludes it; a scoped 'review'/
   * 'fix' still escalates (it is evidence of a real problem).
   */
  scope?: string | null
}

export interface PublishVerdictInput {
  source: VerdictSourceId
  label: string
  verdict: CanonicalVerdict
  score?: number | null
  counts?: VerdictCounts
  worstCheck?: string | null
  startedAt?: number
  finishedAt?: number
  scope?: string | null
}

export interface AppHealth {
  verdict: CanonicalVerdict
  /** Entry carrying the worst verdict (drill-down target). */
  worstSource: VerdictEntry | null
  /** Worst (minimum) score among sources that report one. */
  score: number | null
  counts: VerdictCounts
  staleSources: string[]
  runningSources: string[]
  /** Most recent finishedAt across fresh entries, for "checked …" copy. */
  lastFinishedAt: number | null
}

interface ValidationVerdictStoreState {
  entries: Partial<Record<VerdictSourceId, VerdictEntry>>
  publish: (input: PublishVerdictInput) => void
  markRunning: (source: VerdictSourceId, label: string, startedAt?: number) => void
  markStale: (source: VerdictSourceId, at?: number) => void
  clearAll: () => void
}

const ZERO_COUNTS: VerdictCounts = { pass: 0, warn: 0, fail: 0 }

export const useValidationVerdictStore = create<ValidationVerdictStoreState>()((set) => ({
  entries: {},

  publish: (input) => {
    const finishedAt = input.finishedAt ?? Date.now()
    const entry: VerdictEntry = {
      source: input.source,
      label: input.label,
      verdict: input.verdict,
      score: input.score ?? null,
      counts: input.counts ?? ZERO_COUNTS,
      worstCheck: input.worstCheck ?? null,
      startedAt: input.startedAt ?? finishedAt,
      finishedAt,
      // A fresh publish supersedes any staleness marker.
      staleSince: null,
      scope: input.scope ?? null,
    }
    set((s) => ({ entries: { ...s.entries, [input.source]: entry } }))
  },

  markRunning: (source, label, startedAt = Date.now()) =>
    set((s) => {
      const prev = s.entries[source]
      const entry: VerdictEntry = {
        source,
        label,
        // Keep the last known verdict visible while re-checking.
        verdict: prev?.verdict ?? 'idle',
        score: prev?.score ?? null,
        counts: prev?.counts ?? ZERO_COUNTS,
        worstCheck: prev?.worstCheck ?? null,
        startedAt,
        finishedAt: null,
        staleSince: prev?.staleSince ?? null,
        scope: prev?.scope ?? null,
      }
      return { entries: { ...s.entries, [source]: entry } }
    }),

  markStale: (source, at = Date.now()) =>
    set((s) => {
      const prev = s.entries[source]
      if (!prev || prev.staleSince !== null) return s
      return { entries: { ...s.entries, [source]: { ...prev, staleSince: at } } }
    }),

  clearAll: () => set({ entries: {} }),
}))

export function statusToVerdict(status: ValidationStatus): CanonicalVerdict {
  if (status === 'pass') return 'ok'
  if (status === 'warn') return 'review'
  if (status === 'fail') return 'fix'
  return 'idle'
}

export const VERDICT_RANK: Record<CanonicalVerdict, number> = {
  ok: 0,
  idle: 1,
  review: 2,
  fix: 3,
}

export function deriveAppHealth(
  entries: Partial<Record<VerdictSourceId, VerdictEntry>>,
): AppHealth {
  const list = Object.values(entries).filter((entry): entry is VerdictEntry => Boolean(entry))
  // A scoped run (e.g. 'Block: hero') only validated part of the app: its
  // 'ok'/'idle' must not upgrade to an app-level green vote, but a scoped
  // 'review'/'fix' is real evidence of a problem and still escalates.
  const appScopeList = list.filter(
    (entry) => !entry.scope || entry.verdict === 'review' || entry.verdict === 'fix',
  )
  if (list.length === 0) {
    return {
      verdict: 'idle',
      worstSource: null,
      score: null,
      counts: ZERO_COUNTS,
      staleSources: [],
      runningSources: [],
      lastFinishedAt: null,
    }
  }

  let worst: VerdictEntry | null = null
  for (const entry of appScopeList) {
    if (!worst) {
      worst = entry
      continue
    }
    const rank = VERDICT_RANK[entry.verdict]
    const worstRank = VERDICT_RANK[worst.verdict]
    if (rank > worstRank) worst = entry
    // Prefer an entry that can name its worst check at equal severity.
    else if (rank === worstRank && !worst.worstCheck && entry.worstCheck) worst = entry
  }

  const scores = appScopeList
    .map((entry) => entry.score)
    .filter((score): score is number => typeof score === 'number')
  const counts = appScopeList.reduce<VerdictCounts>(
    (acc, entry) => ({
      pass: acc.pass + entry.counts.pass,
      warn: acc.warn + entry.counts.warn,
      fail: acc.fail + entry.counts.fail,
    }),
    { ...ZERO_COUNTS },
  )
  // Freshness reflects ALL entries (a scoped run still shows 'checking…').
  const finishedTimes = list
    .map((entry) => entry.finishedAt)
    .filter((time): time is number => typeof time === 'number')

  return {
    verdict: worst?.verdict ?? 'idle',
    worstSource: worst,
    score: scores.length > 0 ? Math.min(...scores) : null,
    counts,
    staleSources: list.filter((entry) => entry.staleSince !== null).map((entry) => entry.source),
    runningSources: list.filter((entry) => entry.finishedAt === null).map((entry) => entry.source),
    lastFinishedAt: finishedTimes.length > 0 ? Math.max(...finishedTimes) : null,
  }
}

export function useAppHealth(): AppHealth {
  const entries = useValidationVerdictStore((s) => s.entries)
  return useMemo(() => deriveAppHealth(entries), [entries])
}

export interface HealthSectionStatusLike {
  id: string
  title: string
  status: ValidationStatus
  summary?: string
}

const STATUS_ESCALATION_RANK: Record<ValidationStatus, number> = {
  pass: 0,
  idle: 0, // informational sections never downgrade the verdict
  warn: 1,
  fail: 2,
}

/**
 * Folds detail-section statuses into the displayed app health so the
 * always-visible headline (and pill) can never read BETTER than a section
 * directly below it. Some section inputs (attached/server-stamped
 * ui-conformance models, the latest preview ActionFlow trace) are not
 * published to the verdict store; without this merge a red default-expanded
 * section could sit under a green 'App health OK' headline.
 *
 * Only warn/fail sections escalate — pass/idle sections never change the
 * store-derived verdict.
 */
export function escalateHealthWithSections(
  health: AppHealth,
  sections: HealthSectionStatusLike[],
): AppHealth {
  let worstSection: HealthSectionStatusLike | null = null
  for (const section of sections) {
    if (STATUS_ESCALATION_RANK[section.status] === 0) continue
    if (
      !worstSection ||
      STATUS_ESCALATION_RANK[section.status] > STATUS_ESCALATION_RANK[worstSection.status]
    ) {
      worstSection = section
    }
  }
  if (!worstSection) return health

  const sectionVerdict = statusToVerdict(worstSection.status)
  if (VERDICT_RANK[sectionVerdict] <= VERDICT_RANK[health.verdict]) return health

  return {
    ...health,
    verdict: sectionVerdict,
    worstSource: {
      source: `section:${worstSection.id}`,
      label: worstSection.title,
      verdict: sectionVerdict,
      score: null,
      counts: { ...ZERO_COUNTS },
      worstCheck: worstSection.summary ?? worstSection.title,
      startedAt: health.lastFinishedAt ?? 0,
      finishedAt: health.lastFinishedAt,
      staleSince: null,
      scope: null,
    },
  }
}

/**
 * Honest user-facing label for panel-status words. Keeps code identifiers
 * untouched; only display strings change ('fallback' reads as 'limited').
 */
export function honestStatusLabel(status: string): string {
  return status === 'fallback' ? 'limited' : status
}
